<template>
  <messenger-chat-view-layout
    v-bind="dropZoneProps"
    :chat-state="chatState"
    :agent-typing-count="agentTypingCount"
    :scroll-bar-id="scrollBarId"
    @back="$emit('back')"
    @end-chat="$emit('end-chat')"
    @resize="$emit('resize', $event)"
  >
    <template v-slot:alert>
      <slot name="alert" />
    </template>

    <template v-slot:before-content>
      <widget-file-drop-zone
        class="chat-drop-zone"
        :class="{
          'chat-drop-zone--active': isDragOver,
        }"
        :text="$t('common.dragAndDropToSend')"
        :max-size="maxFileSize"
      />
    </template>

    <messenger-chat-message-list
      :list-items="messageListItems"
      :list-state="messageListState"
      :is-agent-searching="isChatWaitingState(chatState)"
      :first-new-message-id="newMessageState?.firstNewMessageId"
      :new-message-divider-id="newMessageDividerId"
      :is-loading="isChatLoadingState(chatState) || messageListItems.length === 0"
      @load-next-page="$emit('load-next-page')"
    />

    <template v-slot:after-scroll>
      <transition name="fade">
        <div
          v-if="isScrollButtonShown"
          class="chat-wrapper__scroll-button"
        >
          <widget-button
            v-bind="hasNotViewedNewMessages ? themeButtonProps : { color: 'secondary' }"
            is-rounded
            size="32"
            @click="$emit('scroll-button-click')"
          >
            <div class="d-flex align-center gap-1">
              <widget-icon
                name="arrow_downward"
                size="20px"
              />
              {{ scrollButtonText }}
            </div>
          </widget-button>
        </div>
      </transition>
    </template>

    <template v-slot:footer-content>
      <div class="chat-wrapper__footer">
        <div
          v-if="isChatEndedState(chatState)"
          class="pb-4 d-flex flex-column gap-2"
        >
          <widget-button
            v-if="isCloseThisWindowButtonShown"
            v-bind="themeButtonProps"
            class="full-width"
            @click="$emit('close-window')"
          >
            {{ $t('chat.closeThisWindow') }}
          </widget-button>

          <widget-button
            v-if="isStartNewChatButtonShown"
            v-bind="themeButtonProps"
            class="full-width"
            :view="isCloseThisWindowButtonShown ? 'outline' : 'solid'"
            @click="$emit('start-new-chat')"
          >
            {{ $t('chat.startNewChat') }}
          </widget-button>

          <widget-button
            v-if="isLeaveFeedbackButtonShown"
            v-bind="themeButtonProps"
            class="full-width"
            view="outline"
            @click="$emit('leave-feedback')"
          >
            {{ $t('chat.leaveFeedback') }}
          </widget-button>
        </div>

        <widget-form-field-content-editor
          v-else
          name="message"
          attachment-field-name="attachments"
          :autofocus="shouldAutofocusMessageField"
          :autofocus-after-submit="shouldAutofocusMessageField"
          :placeholder="$t('chat.typeYourMessageHere')"
          :is-loading="isChatLoadingState(chatState)"
          :max-length="messengerMessageMaxLength"
          @submit="$emit('submit')"
          @files-picked="$emit('files-picked', $event)"
          @remove-attachment="$emit('remove-attachment', $event)"
          @retry-attachment="$emit('retry-attachment', $event)"
        />
      </div>
    </template>
  </messenger-chat-view-layout>
</template>

<script setup lang="ts">
import type { MessengerChatViewSettings } from '@widgets/messenger/types'
import MessengerChatViewLayout from '@widgets/messenger/components/MessengerChatViewLayout.vue'
import WidgetFormFieldContentEditor from '@widgets/shared/components/fields/WidgetFormFieldContentEditor.vue'
import MessengerChatMessageList from '@widgets/messenger/components/MessengerChatMessageList.vue'
import {
  isChatCommentedState,
  isChatEndedState,
  isChatLoadingState,
  isChatNoAgentsState,
  isChatOfflineState,
  isChatWaitingState,
  type MessengerChatState,
  type MessengerMessageListState,
  type MessengerNewMessageState,
} from '@widgets/messenger/types/chat/chat'
import type { MessengerMessageListItem } from '@widgets/messenger/types/chat/message'
import WidgetButton from '@widgets/shared/components/WidgetButton.vue'
import translateService from '@widgets/shared/services/translate/translateService'
import WidgetIcon from '@widgets/shared/components/WidgetIcon.vue'
import { messengerMessageMaxLength } from '@widgets/messenger/const'
import { useDragAndDropFiles } from '@widgets/messenger/compositions/useDragAndDropFiles'
import WidgetFileDropZone from '@widgets/shared/components/WidgetFileDropZone.vue'
import { useWidgetTheme } from '@widgets/messenger/compositions/useWidgetTheme'
import { computed } from '@/composition/vue/compositionApi'

const {
  chatState,
  isSatisfactionSurveyShown,
  newMessageState,
  numberOfNewMessages = 0,
  shouldAutofocusMessageField = true,
} = defineProps<{
  viewSettings: MessengerChatViewSettings
  chatState: MessengerChatState
  agentTypingCount: number
  messageListItems: MessengerMessageListItem[]
  messageListState: MessengerMessageListState
  isSatisfactionSurveyShown: boolean
  maxFileSize: number // in bytes
  scrollBarId?: string
  newMessageDividerId?: string
  newMessageState?: MessengerNewMessageState
  isScrollButtonShown?: boolean
  numberOfNewMessages?: number
  shouldAutofocusMessageField?: boolean
}>()

const emit = defineEmits<{
  (e: 'back'): void
  (e: 'end-chat'): void
  (e: 'resize', height: number): void
  (e: 'submit'): void
  (e: 'start-new-chat'): void
  (e: 'leave-feedback'): void
  (e: 'close-window'): void
  (e: 'scroll-button-click'): void
  (e: 'files-picked', files: File[]): void
  (e: 'remove-attachment', attachmentId: string): void
  (e: 'retry-attachment', attachmentId: string): void
  (e: 'load-next-page'): void
}>()

const { themeButtonProps } = useWidgetTheme()

const { dropZoneProps, isDragOver } = useDragAndDropFiles((files) => {
  emit('files-picked', files)
})

const isNoAgents = computed(() => {
  return isChatNoAgentsState(chatState)
})

const isSurveyCompleted = computed(() => {
  return isChatCommentedState(chatState)
})

const isOfflineMode = computed(() => {
  return isChatOfflineState(chatState)
})

const isCloseThisWindowButtonShown = computed(() => {
  return isNoAgents.value || isOfflineMode.value
})

const isStartNewChatButtonShown = computed(() => {
  return !isOfflineMode.value
})

const isLeaveFeedbackButtonShown = computed(() => {
  return !isNoAgents.value && isSatisfactionSurveyShown && !isSurveyCompleted.value && !isOfflineMode.value
})

const hasNotViewedNewMessages = computed(() => newMessageState && !newMessageState.isViewed)
const scrollButtonText = computed(() => {
  if (hasNotViewedNewMessages.value) {
    if (numberOfNewMessages === 1) {
      return translateService.t('chat.newMessage')
    }
    return translateService.t('chat.newMessages', {
      count: numberOfNewMessages > 9 ? '9+' : numberOfNewMessages,
    })
  }
  return translateService.t('chat.goToLatestMessage')
})
</script>

<style lang="scss" scoped>
@import '@/styles/custom-variables.scss';
@import '@/styles/mixins.scss';

$sidePadding: 16px;
$dropZonePadding: 16px;

.chat-wrapper {
  display: flex;
  flex-direction: column;
  max-height: 100%;
  min-height: 0;

  &__scroll-button {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
  }

  &__footer {
    flex-shrink: 0;
    flex-grow: 0;
    padding: 0 $sidePadding;
  }
}

.chat-drop-zone {
  position: absolute;
  top: $dropZonePadding;
  left: $dropZonePadding;
  width: calc(100% - 2 * $dropZonePadding);
  height: calc(100% - $dropZonePadding);
  z-index: 1;
  opacity: 0;
  pointer-events: none;
  transition: opacity $transition-200ms-ease-in-out;

  &--active {
    opacity: 1;
    pointer-events: all;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity $transition-100ms-ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
